import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from './useSuiteStore'
import { useAuthStore } from './useAuthStore'
import { withApiState } from '@/utils/withApiState'

export interface Ticket {
  [key: string]: any
}

export const useTicketStore = defineStore('ticket', () => {
  const tickets = ref<Ticket[]>([])
  const ticket = ref<Ticket | null>(null)
  const loading = ref(false)

  const suiteStore = useSuiteStore()

  async function fetchAllTickets(params?: { search?: string; statuses?: string[] }) {
    loading.value = true
    try {
      let path = '/tickets'

      if (params?.search || params?.statuses) {
        const searchParams = new URLSearchParams()
        if (params?.search) searchParams.append('search', params.search)
        if (params?.statuses) params.statuses.forEach(status => searchParams.append('ticketStatus', status))

        path += `?${searchParams.toString()}`
      }

      const response = await suiteStore.suiteFetch(path, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        },
        credentials: 'include'
      })

      if (!response || !response.ok) throw new Error(`Echec de la récupération des tickets`)

      const data = await response.json()
      tickets.value = data
      return data
    } catch (err) {
      // toast removed
    } finally {
      loading.value = false
    }
  }

  async function createTicket(ticket: { title: string; description: string; files?: File[] }) {
    loading.value = true

    try {
      const formData = new FormData()
      formData.append('title', ticket.title)
      formData.append('description', ticket.description)

      if (ticket.files && ticket.files.length > 0) {
        ticket.files.forEach(file => {
          formData.append('files', file)
        })
      }

      const response = await suiteStore.suiteFetch('/tickets', {
        method: 'POST',
        headers: {
          'x-csrf-token': useAuthStore().getCoreCsrfToken()
        },
        credentials: 'include',
        body: formData
      })

      if (!response || !response.ok) throw new Error(`Echec de la création du ticket`)

      const data = await response.json()
      tickets.value.push(data)

      return data
    } catch (err) {
      // toast removed
    } finally {
      loading.value = false
    }
  }

  async function fetchTicketById(ticketId: string) {
    loading.value = true

    try {
      const response = await suiteStore.suiteFetch(`/tickets/${ticketId}/public`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      })

      if (!response || !response.ok) throw new Error(`Echec de la récupération du ticket`)

      const data = await response.json()

      const descriptionComment = {
        id: 'ticket-description',
        author: `${data.ticketRequester.user.firstName} ${data.ticketRequester.user.lastName}`,
        date: new Date(data.dateCreated).getTime(),
        comment: data.description,
        supportComment: false,
        attachments: data.attachments || []
      }

      data.comments.unshift(descriptionComment)

      ticket.value = data
      return data
    } catch (err) {
      // toast removed
    } finally {
      loading.value = false
    }
  }

  /**
   * Add a new comment to a ticket
   * Falls back to optimistic local update if backend does not return the full updated ticket
   */
  async function addComment(ticketId: string, comment: string, files?: File[]) {
    loading.value = true

    try {
      const formData = new FormData()
      formData.append('comment', comment)

      if (files && files.length > 0) {
        files.forEach(file => {
          formData.append('files', file)
        })
      }

      const response = await suiteStore.suiteFetch(`/tickets/${ticketId}/comment`, {
        method: 'POST',
        headers: {
          'x-csrf-token': useAuthStore().getCoreCsrfToken()
        },
        credentials: 'include',
        body: formData
      })

      if (!response || !response.ok) throw new Error(`Echec de l'ajout du commentaire`)

      const data = await response.json().catch(() => null)

      // Refresh ticket data after adding a comment
      await fetchTicketById(ticketId)

      return data
    } catch (err) {
      // toast removed
    } finally {
      loading.value = false
    }
  }

  /**
   * Resolve a ticket with a comment
   */
  async function resolveTicket(ticketId: string, comment: string) {
    return withApiState(
      async () => {
        const response = await suiteStore.suiteFetch(`/tickets/${ticketId}/resolve`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-csrf-token': useAuthStore().getCoreCsrfToken()
          },
          credentials: 'include',
          body: JSON.stringify({ comment })
        })

        if (!response ||!response.ok) throw new Error(`Echec de la résolution du ticket`)

        const data = await response.json().catch(() => null)
        if (data && data.ticket) {
          ticket.value = data.ticket
        } else {
          // Refresh full ticket if possible
          await fetchTicketById(ticketId)
        }
        return data
      },
      { loading },
    )
  }

  return {
    tickets,
    ticket,
    loading,
    fetchAllTickets,
    fetchTicketById,
    createTicket,
    addComment,
    resolveTicket
  }
})
